import { BCMSCurrency, BrandingStyle, DisabledPrompt, useBCMSModal, useStateManager } from "@bcm-dev/bcms-vite";
import CustomAccordion, { CustomAccordionContent, CustomAccordionToggle } from "components/custom-ui/CustomAccordion";
import CustomSelect, { CustomSelectItem } from "components/custom-ui/CustomSelect";
import { FormContainer, FormRowView } from 'components/shared';
import IconToggle from "components/shared/views/IconToggle";
import { PrimaryButton, SecondaryButton } from 'components/ui';
import { SVGAsset } from "res";

/**
 * @function TASPositivePayExceptionChecksMobileContentView
 * @return {React.Component}
 * @exported true
 * @description The "TAS Positive Pay Exceptions" mobile content view.
 */
export default function TASPositivePayExceptionChecksMobileContentView(props) {
    const { data, ui, onCancelClick, onSaveClick } = useStateManager();
    const checkForms = data.exceptionCheckForms;
    const checkFormsUI = ui.exceptionCheckForms;
    const userEditedCheckForms = data.search.userEditedCheckForms().length > 0;
    const { open } = useBCMSModal();

    const rows = checkForms.reduce((acc, item, index) => {
        const amount = item.amount ? item.amount.amount : undefined;
        const isEditing = checkFormsUI[index].isEditing;
        const editItem = data.search.editExceptionCheckForms.filter(_item => _item.id === item.id)[0];
        
        acc.push(
            isEditing
            ? <EditingRow key={ item.id } amount={ amount  } item={ item } editItem={ editItem }/>
            : <Row key={ item.id } amount={ amount  } item={ item }/>
        );
        return acc;
    }, []);

    const onYesClick = evt => {
        onCancelClick();
    }

    const openCancelModal = val => {
        open("TASPositivePayCancelEditChangeModal", { onYesClick: onYesClick } );
    };
    
    return (
        <div className="tas-positive-pay-exception-checks-mobile-content-view-container">
            { rows }
            { userEditedCheckForms &&
            <div className="flex-container flex-horizontal button-container search-results-button-container">
                <SecondaryButton className="set-width" text="Cancel" onClick={ openCancelModal } />
                <PrimaryButton className="set-width" text="Save" data-point="Search_Button" onClick={ onSaveClick } />
            </div>
            }
        </div>
    );
}

/**
 * @function EditingRow
 * @return {React.Component}
 * @exported false
 * @description A row that can be edited by the user.
 */
function EditingRow(props) {
  const { data, ui, onEdit, onMobileCloseButtonClick, onReturnReasonClick } = useStateManager();
  const item = props.editItem ?? {};
  const uiItem = ui.exceptionCheckForms.filter(_item => _item.exceptionCheckForm.id === item.id)[0];
  const amount = props.amount ?? "";
  const amountFormatted = BCMSCurrency.init(amount).format();
    const search = data.search;
  const showReturnReasonSelect = uiItem.showReturnReasonSelect;
  const returnReasonHasError = uiItem.returnReasonHasError;
  const returnReasonSelectClasses = returnReasonHasError
    ? { className: `return-reason-select select-error` }
    : { className: `return-reason-select` };
  const accountItems = search.accounts.reduce((acc, item) => {
      acc.push(
          <CustomSelectItem key={ item.id } value={ item.id }>{ item.name }</CustomSelectItem>
      )
      return acc;
  }, []);
  const chevronIconClosedClass = uiItem.mobile.isActive ? "" : "closed";
  const account = item.account;

  return (
    <div key={ item.id } className="show-on-mobile">
        <div className="flex-container align-top">
            <CustomAccordion isOpen={ uiItem.mobile.isActive }>
                <div className="flex-container flex-horizontal align-top check-custom-accordion-content">
                        <div className="set-width">
                            <CustomAccordionToggle className={ "" } onClick={ onMobileCloseButtonClick(item.id) }>
                                <IconToggle className={ `chevron-icon-container ${ chevronIconClosedClass }`} icon="DownChevronIcon" text=""/>
                            </CustomAccordionToggle>
                        </div>
                        <div className="flex-width">
                            <div className="flex-container flex-vertical check-details">
                                <div className="check-detail-row">
                                    <span className="sub-text">Account: </span>
                                    <b>{ account.nickname } - { account.number }</b>
                                </div>
                                <div className="check-detail-row">
                                    <span className="sub-text">Issued Date: </span>
                                    <b>{ item.issuedDate.format("m/d/Y") }</b>
                                </div>
                                <div className="check-detail-row">
                                    <span className="sub-text">Check Number: </span>
                                    <DisabledPrompt>
                                        <a className="clickable">
                                            <span>
                                                <b>{ `${ item.checkNumber }` }</b>
                                            </span>
                                        </a>
                                    </DisabledPrompt>
                                </div>
                                <div className="check-detail-row">
                                    <span className="sub-text">Amount: </span>
                                    <b>{ amountFormatted }</b>
                                </div>
                            </div>
                        </div>
                </div>
                <CustomAccordionContent className="flex-container flex-vertical tas-positive-pay-add-check-page-accordion-content">
                    <CustomAccordionToggle className="flex-container flex-width close-accordion accordion-edit-view" onClick={ onMobileCloseButtonClick(item.id) }>
                        <BrandingStyle className="flex-width sub-text" element={<p >DETAILS</p>}/>
                        <SVGAsset className="icon set-width" id="CloseX" data-point="CloseButton" />
                    </CustomAccordionToggle>
                    <FormContainer className="small-form-width">
                        <FormRowView className="sub-text" text="Account">
                            <b>{ `${ item.account.name } ${ item.account.number }` }</b>
                        </FormRowView>
                        <FormRowView className="sub-text" text="Issued Date">
                            <b>{ item.issuedDate.format("m/d/Y") }</b>
                        </FormRowView>
                        <FormRowView className="sub-text" text="Check Number">
                            <b>{ item.checkNumber }</b>
                        </FormRowView>
                        <FormRowView className="sub-text" text="Amount">
                            <b>{ amountFormatted }</b>
                        </FormRowView>
                        <FormRowView className="sub-text" text="Payee">
                            <b>{ item.payee }</b>
                        </FormRowView>
                        <FormRowView className="sub-text reason" text="Reason">
                            <b>{ item.reason }</b>
                        </FormRowView>
                        <div className="flex-container flex-horizontal">
                            <FormRowView className="flex-width action-row-view" text="Action">
                                <CustomSelect active={  uiItem.action.isActive } value={ item.action }
                                dataPoint={ `TAS_Positive_Pay_View_Checks_Desktop_Edit_Row_Type_${ item.id }`}
                                onChange={ onEdit(item.id, "action") }>
                                    <CustomSelectItem value="prompt" className="prompt" 
                                    dataPoint={ `TAS_Positive_Pay_Exception_Checks_Mobile_Edit_Row_Action_Option_Prompt_${ item.id }` }></CustomSelectItem>
                                    <CustomSelectItem value="approve">Approve</CustomSelectItem>
                                    <CustomSelectItem value="return">Return</CustomSelectItem>
                                </CustomSelect>
                                
                            </FormRowView>
                            <FormRowView className="flex-width" text={ `${showReturnReasonSelect ? "Return Reason" : ""}`}>
                                { showReturnReasonSelect && 
                                <span onClick={ onReturnReasonClick(item.id) }>
                                    <CustomSelect { ...returnReasonSelectClasses } active={  uiItem.returnReason.isActive } value={ item.returnReason }
                                    dataPoint={ `TAS_Positive_Pay_View_Checks_Desktop_Edit_Row_Type_${ item.id }`}
                                    onChange={ onEdit(item.id, "returnReason") }>
                                        <CustomSelectItem value="forgery">Forgery</CustomSelectItem>
                                        <CustomSelectItem value="altered/fictitious">Altered/Fictitious</CustomSelectItem>
                                        <CustomSelectItem value="duplicate presentmen">Duplicate Presentmen</CustomSelectItem>
                                        <CustomSelectItem value="refer to maker">Refer to Maker</CustomSelectItem>
                                    </CustomSelect>
                                </span>
                                }
                            </FormRowView>
                        </div>
                        { returnReasonHasError && 
                        <div className="error-alert-info-container">
                            <SVGAsset id="AlertTriangleIcon" className="" />
                            <span>{ `Please provide return reason(s).` }</span>
                        </div> 
                        }
                    </FormContainer>
                </CustomAccordionContent>
            </CustomAccordion>
        </div>
    </div>
  );
}

/**
 * @function Row
 * @return {React.Component}
 * @exported false
 * @description A row that can only be read by the user.
 */
function Row(props) {
  const { ui, onChange, onEditClick, onMobileCloseButtonClick } = useStateManager();
  const item = props.item ?? {};
  const uiItem = ui.exceptionCheckForms.filter(_item => _item.id === item.id)[0];
  const amount = props.amount ?? "";
  const amountFormatted = BCMSCurrency.init(amount).format();
  const chevronIconClosedClass = uiItem.mobile.isActive ? "" : "closed";
  const account = item.account;

  return (
    <div key={ item.id } className="show-on-mobile">
        <div className="flex-container align-top">
            <CustomAccordion isOpen={ uiItem.mobile.isActive }>
                <div className="flex-container flex-horizontal align-top check-custom-accordion-content">
                        <div className="set-width">
                            <CustomAccordionToggle className={ "" } onClick={ onMobileCloseButtonClick(item.id) }>
                                <IconToggle className={ `chevron-icon-container ${ chevronIconClosedClass }`} icon="DownChevronIcon" text=""/>
                            </CustomAccordionToggle>
                        </div>
                        <div className="flex-width">
                            <div className="flex-container flex-vertical check-details">
                                <div className="check-detail-row">
                                    <span className="sub-text">Account: </span>
                                    <b>{ account.nickname } - { account.number }</b>
                                </div>
                                <div className="check-detail-row">
                                    <span className="sub-text">Issued Date: </span>
                                    <b>{ item.issuedDate.format("m/d/Y") }</b>
                                </div>
                                <div className="check-detail-row">
                                    <span className="sub-text">Check Number: </span>
                                    <b>{ item.checkNumber }</b>
                                </div>
                                <div className="check-detail-row">
                                    <span className="sub-text">Amount: </span>
                                    <b>{ amountFormatted }</b>
                                </div>
                            </div>
                        </div>
                </div>
                <CustomAccordionContent className="flex-container flex-vertical">
                    <CustomAccordionToggle className="flex-container flex-width close-accordion accordion-edit-view" onClick={ onMobileCloseButtonClick(item.id) }>
                        <BrandingStyle className="flex-width sub-text" element={<p >DETAILS</p>}/>
                        <SVGAsset className="icon set-width" id="CloseX" data-point="CloseButton" />
                    </CustomAccordionToggle>
                    <FormContainer className="small-form-width">
                        
                        <FormRowView className="sub-text" text="Account">
                            <b>{ `${ item.account.name } ${ item.account.number }` }</b>
                        </FormRowView>
                        <FormRowView className="sub-text" text="Issued Date">
                            <b>{ item.issuedDate.format("m/d/Y") }</b>
                        </FormRowView>
                        <FormRowView className="sub-text" text="Check Number">
                            <b>{ item.checkNumber }</b>
                        </FormRowView>
                        <FormRowView className="sub-text" text="Amount">
                            <b>{ amountFormatted }</b>
                        </FormRowView>
                        <FormRowView className="sub-text" text="Payee">
                            <b>{ item.payee }</b>
                        </FormRowView>
                        <FormRowView className="sub-text" text="Reference Number">
                            <b>{ item.referenceNumber }</b>
                        </FormRowView>
                        <FormRowView className="sub-text type" text="Type">
                            <b>{ item.type }</b>
                        </FormRowView>
                        <FormRowView className="sub-text type type-form-view" text="Status">
                            <b>{ item.status }</b>
                        </FormRowView>
                        <div className="flex-container flex-vertical">
                            <a className="clickable edit-link" onClick={ onEditClick(item.id) }>Edit</a>
                            <DisabledPrompt>
                                <a className="clickable">Delete</a>
                            </DisabledPrompt>
                        </div>
                    </FormContainer>
                </CustomAccordionContent>
            </CustomAccordion>
        </div>
    </div>
  );
}